import { IonRouterLink } from "@ionic/react";
import { ComponentProps } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import ActionIconButton from "@components/buttons/ActionIconButton";
import Button from "@components/buttons/Button";
import WebinarRegistrationButton from "@components/buttons/WebinarRegistrationButton";
import styles from "@components/cards/ArticleCard.module.css";
import BasicCard from "@components/cards/BasicCard";
import DateAndTimeStamp from "@components/dates/DateAndTimeStamp";
import Tag from "@components/labels/Tag";
import PinToggle from "@components/PinToggle";
import Icon from "@components/text/Icon";
import BodyCopy from "@components/typography/BodyCopy";
import Heading2 from "@components/typography/Heading2";
import InternalLink from "@components/typography/InternalLink";
import useMyExpert from "@hooks/useMyExpert";
import { ArticleContentTypeEnum, FocusAreaEnum, GetArticlesBasicQuery, PinResourceTypeEnum } from "@typing/Generated";
import { articleHasVideo, tagPropsForFocusArea } from "@utils/modelUtils";
import { journeyRoute } from "@utils/routeUtils";

type ArticleBasicType = NonNullable<
  NonNullable<NonNullable<GetArticlesBasicQuery["articles"]["edges"]>[number]>["node"]
>;

type Props = {
  article: ArticleBasicType;
  border?: "danger-300";
  focusArea?: FocusAreaEnum;
  goal?: ComponentProps<typeof PinToggle>["goal"];
  journeyId: string | null;
  onRemove?: (articleId: string) => void;
  webinarSmall?: boolean;
};

const ArticleCard = ({ article, border, focusArea, goal, journeyId, onRemove, webinarSmall = false }: Props) => {
  const intl = useIntl();

  const myExpert = useMyExpert();

  const isIncompleteWebinar = article.contentType === ArticleContentTypeEnum.WEBINAR && !article.webinarCompleted;

  const articleRoute = journeyRoute({
    journeyId: journeyId ?? "",
    linkAfterJourney: `/articles/${article.id}`,
    myExpert
  });

  if (isIncompleteWebinar) {
    return (
      // eslint-disable-next-line react/forbid-elements
      <BasicCard border={border} padding="none">
        <div className={`${styles.webinar} ${webinarSmall ? styles.webinarSmall : ""}`}>
          {article.photoUrl && <img className={styles.webinarPhoto} src={article.photoUrl} />}
          <div className={styles.webinarText}>
            <Heading2>
              <InternalLink to={articleRoute}>{article.title}</InternalLink>
            </Heading2>
            {!webinarSmall && (
              <div className={styles.webinarTag}>
                <Tag
                  color="gray-50"
                  text={intl.formatMessage({ id: "pages.explore.events.onlineEvent" })}
                  textColor="black"
                />
              </div>
            )}

            {!webinarSmall && (
              <div>
                <BodyCopy color="gray">{article.blurb}</BodyCopy>
              </div>
            )}

            <div className={styles.webinarStartTime}>
              {!webinarSmall && (
                <BodyCopy color="gray">
                  <FormattedMessage id="pages.explore.events.startTime" />
                </BodyCopy>
              )}
            </div>
            <div>
              <BodyCopy color="primary">
                <DateAndTimeStamp showWeekday value={article.webinarStartTime} />
              </BodyCopy>
            </div>
            <div className={styles.register}>
              <WebinarRegistrationButton article={article} journeyId={journeyId} />
              <Button routerLink={articleRoute}>
                <FormattedMessage id="pages.explore.events.details" />
              </Button>
            </div>
          </div>
        </div>
      </BasicCard>
    );
  }

  return (
    // eslint-disable-next-line react/forbid-elements
    <IonRouterLink className={styles.spacing} data-test="article-card" routerLink={articleRoute}>
      {article.photoUrl && (
        <div className={`${styles.container} ${border ? styles.editModeBorder : ""}`}>
          <div
            className={styles.article}
            style={{
              backgroundImage: `linear-gradient(0deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.2) 100%), url(${
                article.photoUrl ?? ""
              })`,
              backgroundRepeat: "no-repeat",
              backgroundSize: "cover"
            }}
          >
            <div className={styles.overlay}></div>
            <div className={styles.toggles}>
              {articleHasVideo(article) && (
                <div className={styles.videoCircle}>
                  <Icon icon="playCircle" />
                </div>
              )}
              {journeyId && (
                <PinToggle
                  goal={goal}
                  journeyId={journeyId}
                  resourceId={article.id}
                  resourceType={PinResourceTypeEnum.ARTICLE}
                />
              )}
              {onRemove && journeyId && (
                <ActionIconButton
                  ariaLabelKey="accessibility.ariaLabels.delete"
                  color="white"
                  icon="times"
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    onRemove(article.id);
                  }}
                />
              )}
            </div>
            {(focusArea || article.focusAreas.length > 0) && (
              <div className={styles.tag}>
                <Tag {...tagPropsForFocusArea(focusArea ?? article.focusAreas[0], intl)} />
              </div>
            )}

            <div className={styles.text + " " + styles.hideMobile}>
              <Heading2 color="white">{article.title}</Heading2>
            </div>
            <div className={styles.text + " " + styles.showMobile}>
              <BodyCopy bold color="white">
                {article.title}
              </BodyCopy>
            </div>
          </div>
        </div>
      )}
    </IonRouterLink>
  );
};

export default ArticleCard;
